import { computed } from "vue";
import type { LoaderProps, LoaderColor } from "./loader.types";
import type { Sizes } from "@/runtime/types";
import { useSharedState } from "composables/useSharedState";
import { loaderCircleClasses, loaderBarTrackClasses, loaderBarFillClasses } from "./loader.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { parseBoolAttr } from "@/runtime/shared/attrs";

const LOADER_VARIANTS = ["circle", "bar"] as const;
type LoaderVariant = (typeof LOADER_VARIANTS)[number];

export interface UseLoaderOptions {
  props: Readonly<LoaderProps>;
}

export const useLoader = (options: UseLoaderOptions) => {
  const { props } = options;
  const state = useSharedState(props);

  const variant = computed<LoaderVariant>(() => {
    const v = state.variant?.value;
    return (LOADER_VARIANTS as readonly string[]).includes(v as string) ? (v as LoaderVariant) : "circle";
  });

  const size = computed(() => (state.size?.value as Sizes) ?? "md");
  const color = computed(() => (state.color?.value as LoaderColor) ?? "mono");
  const complete = computed(() => parseBoolAttr(state.complete?.value as boolean | string | undefined, false));

  const classes = computed(() => {
    const base =
      variant.value === "bar"
        ? loaderBarTrackClasses({ size: size.value, color: color.value })
        : loaderCircleClasses({ size: size.value, color: color.value });
    return mergeClassProp(base, props.class);
  });

  const fillCls = computed(() => loaderBarFillClasses({ complete: complete.value ? "true" : "false" }));

  return { variant, classes, fillCls };
};
